import { ReactElement } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Sizes,
} from 'react-bootstrap';
import useAppDispatch from '../../hooks/useAppDispatch';
import useAppSelector from '../../hooks/useAppSelector';
import { removeActiveModal } from '../../store/slices/modal';
import ModalContextProvider from './ModalContextProvider';
import { ModalWrapper } from './ModalRenderer.styles';

interface Props {
  modalId: string;
  body?: ReactElement | string;
  customContent?: ReactElement | string;
  footer?: ReactElement;
  title?: ReactElement | string;
  size?: Sizes;
  stackIndex: number;
  width?: number;
  backdrop?: Modal.ModalProps['backdrop'];
}

const ModalRenderer = ({
  modalId,
  body,
  customContent,
  footer,
  size,
  stackIndex,
  title,
  width,
  backdrop,
}: Props) => {
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector(
    (state) => state.modal.activeModals[modalId],
  );
  const isOpen = !!activeModal;

  if (!isOpen) {
    return null;
  }

  const onHide = () => {
    activeModal?.onHide?.();

    if (activeModal?.closeOnHide) {
      dispatch(removeActiveModal({ modalId }));
    }
  };

  return (
    <ModalWrapper
      // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      $width={width}
      keyboard={backdrop !== 'static'}
      $stackIndex={stackIndex}
      show={isOpen}
      onHide={onHide}
      data-testid={modalId}
      animation={false}
      backdropClassName={`modal-backdrop-${stackIndex + 1}`}
      bsSize={size}
      backdrop={backdrop}
    >
      <ModalContextProvider modalId={modalId}>
        {title && (
          <ModalHeader closeButton>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
        )}
        {customContent}
        {body && <ModalBody>{body}</ModalBody>}
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContextProvider>
    </ModalWrapper>
  );
};

ModalRenderer.defaultProps = {
  body: null,
  customContent: null,
  footer: null,
  size: '',
  title: '',
  width: null,
  backdrop: undefined,
};

export default ModalRenderer;
